import React, { useEffect, useState } from 'react';
import styles from './products.module.scss';
import cx from 'classname';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProducts } from 'store/actions/products';
import {
  EmptyChartIcon,
  ProductListIcon,
  ProductGridIcon,
} from 'components/icons';
import Status from 'components/common/status';
import ProductGridView from './ProductGridView';
import ProductTableView from './ProductTableView';
import { ADMIN, formatDateForApiUtc, OWNER, SUPPORT_OFFICER } from 'libs';
import { useHistory } from 'react-router';
import { globalStyles } from 'styles';
import DashboardLayout from 'layout/dashboardLayout';
import Fetching from 'components/Fetching';
import classname from 'classname';
import DateFilter from 'components/DateFilter';
import Button from 'components/button';
import composeHooks from 'react-hooks-compose';
import FirebaseAnalytics, { AnalyticsLogStatus } from 'libs/analytics';
import { getDeviceType } from 'libs';

const useForm = () => {
  const [view, setView] = useState('grid');
  const loading = useSelector((state) => state.products.products.inProgress);
  return {
    view,
    setView,
    loading,
  };
};

const ProductsList = ({ view, setView, loading, ...props }) => {
  const productType = useSelector(
    (state) => state.products.products.productType,
  );
  const products = useSelector((state) => state.products.products.data);

  const history = useHistory();
  const dispatch = useDispatch();
  const { meta, pagination, filters } = useSelector(
    (state) => state.products.products,
  );

  const [index, setIndex] = useState('All');
  const [date, setDate] = useState({ from: '', to: '' });
  const roleChecker = useSelector((state) => state.common.user);

  useEffect(() => {
    if (date.from && date.to) {
      const formatedStartDate = formatDateForApiUtc(date.from);
      const formatedEndDate = formatDateForApiUtc(date.to);

      const params = {
        ...pagination,
        ...filters,
        ...(formatedStartDate && { from: formatedStartDate }),
        ...(formatedEndDate && { to: formatedEndDate }),
      };

      dispatch(fetchProducts(params, productType));
    }
  }, [fetchProducts, filters, pagination, productType, date]);

  const filterByStatus = (res) => {
    setIndex(res);
    dispatch(
      fetchProducts({
        ...pagination,
        status:
          res === 'All'
            ? null
            : res === 'Pending'
            ? 'PENDING_APPROVAL'
            : res === 'Disabled'
            ? 'DISABLED'
            : res.toUpperCase(),
      }),
    );
  };

  const handleOnCreateProduct = () => {
    FirebaseAnalytics.logWithValue('create_product', {
      STATUS: AnalyticsLogStatus.SUCCESS,
      DEVICE_TYPE: getDeviceType(),
    });
    history.push('/products/choose-template');
  };

  useEffect(() => {
    if (roleChecker?.merchantUser?.role === SUPPORT_OFFICER) {
      history.push('/dashboard');
    }
  }, [history, roleChecker]);

  const canCreate =
    roleChecker?.merchantUser?.role === OWNER ||
    roleChecker?.merchantUser?.role === ADMIN;
  return (
    <DashboardLayout>
      <div
        className={cx(globalStyles.flex, globalStyles.justifyContentBetween)}
      >
        <div>
          <DateFilter date={date} setDate={setDate} defaultValue={'allTime'} />
        </div>

        <div>
          <div
            className={classname(
              globalStyles.flex,
              globalStyles.alignItemsCenter,
            )}
          >
            <div className={cx(styles.statusFilter, globalStyles.mr24)}>
              <Status
                status={[
                  'Active',
                  'Disabled',
                  'Archived',
                  'Deleted',
                  'Pending',
                  'Draft',
                ]}
                firstItem="All"
                handleClick={filterByStatus}
              />
            </div>
            <div className={cx(styles.statusFilterMobile)}>
              <Status
                status={[
                  'All',
                  'Active',
                  'Disabled',
                  'Archived',
                  'Deleted',
                  'Pending',
                  'Draft',
                ]}
                variant="blue"
                active="All"
                handleClick={filterByStatus}
              />
            </div>
            <div className={cx(styles.dontShow)}>
              <div className={cx(styles.viewContainer, globalStyles.mr24)}>
                <div
                  className={
                    view === 'grid' ? styles.viewActive : styles.viewInactive
                  }
                >
                  <ProductGridIcon
                    className={styles.actGrid}
                    data-testid="grid-button"
                    onClick={() => setView('grid')}
                  />
                </div>
                <div
                  className={
                    view === 'list' ? styles.viewActive : styles.viewInactive
                  }
                >
                  <ProductListIcon
                    onClick={() => setView('list')}
                    data-testid="list-button"
                  />
                </div>
              </div>
            </div>
            <div>
              {canCreate ? (
                <Button
                  onClick={handleOnCreateProduct}
                  className={styles.createBtn}
                  disabled={roleChecker?.merchantUser?.role === SUPPORT_OFFICER}
                  text="Create Product"
                  variant="primary"
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      {loading && <Fetching />}
      {products && products.length === 0 && !loading && (
        <div className={styles.empty}>
          <EmptyChartIcon />
          <h3>There’s nothing to see here</h3>
          <p>
            Your haven’t created a product yet. Use the button below to get
            started
          </p>
          {canCreate ? (
            <Button
              onClick={handleOnCreateProduct}
              className={styles.createBtnEmpty}
              disabled={roleChecker?.merchantUser?.role === SUPPORT_OFFICER}
              text="Create Product"
              variant="primary"
              id="create-product-button-list"
            />
          ) : null}
        </div>
      )}
      {products && products.length > 0 && !loading && (
        <>
          {view === 'grid' ? (
            <ProductGridView products={products} testid="grid" />
          ) : (
            <ProductTableView
              products={products}
              meta={meta}
              filters={filters}
              pagination={pagination}
              productType={productType}
              fetchProducts={fetchProducts}
              testid="list-view"
            />
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default composeHooks({ useForm })(ProductsList);
